import * as React from 'react';
import { endOfDay, startOfDay } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  DateRangePicker,
  SearchForm,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { FilesArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { AUDIT_CATEGORIES, describeAudit, formatWhen, matchesAudit } from '@/lib/audit';
import { useAuditLog } from '@/lib/engagement-api';

const ALL = '__all__';

export function AuditPage() {
  const [category, setCategory] = React.useState<string>(ALL);
  const prefixes = AUDIT_CATEGORIES.find((c) => c.label === category)?.prefixes;
  const [query, setQuery] = React.useState('');
  const [range, setRange] = React.useState<DateRange | undefined>();
  const { data, isPending, error } = useAuditLog({
    ...(prefixes ? { actions: prefixes.join(',') } : {}),
    // Whole days in the viewer's own timezone, so "26 Oct" means the 26th as HR reads it.
    ...(range?.from
      ? { from: startOfDay(range.from).toISOString(), to: endOfDay(range.to ?? range.from).toISOString() }
      : {}),
  });
  const rows = (data ?? []).filter((r) => matchesAudit(r, query));
  // The API returns the latest 500; past that, older rows only show with a narrower date range.
  const capped = (data ?? []).length >= 500;

  return (
    <PageContainer>
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4 sm:py-4">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px]" aria-label="Show changes to">
              <SelectValue placeholder="All changes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All changes</SelectItem>
              {AUDIT_CATEGORIES.map((c) => (
                <SelectItem key={c.label} value={c.label}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateRangePicker className="w-[220px]" value={range} onChange={setRange} />
          <SearchForm
            className="min-w-[220px] flex-1"
            placeholder="Search name, employee or action"
            onSearch={setQuery}
          />
          {data && (
            <p className="text-sm text-muted-foreground">
              {rows.length} shown{capped && ' · latest 500 only, pick dates to see older'}
            </p>
          )}
          <p className="basis-full text-xs text-muted-foreground">
            Every important change — ratings, goals, roles, review windows and improvement plans —
            with who made it, for whom, and when. Simply opening a page is not recorded.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {isPending && <Skeleton className="m-4 h-40" />}
          {error && (
            <Alert variant="destructive" className="m-4">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          {!isPending && !error && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[170px]">When</TableHead>
                    <TableHead>Done by</TableHead>
                    <TableHead>What happened</TableHead>
                    <TableHead>Employee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <EmptyState
                          art={FilesArt}
                          title={query.trim() || range ? 'Nothing matches these filters' : 'Nothing recorded yet'}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row) => {
                    const { label, details } = describeAudit(row);
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatWhen(row.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {/* Null after the actor is deleted - SetNull on purpose, so
                              removing a person cannot erase what they did. */}
                          {row.actor?.name ?? <span className="text-muted-foreground">System</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{label}</div>
                          {details && <div className="text-muted-foreground">{details}</div>}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.subject ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
